import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api-client'
import type {
  Action,
  ActionAuditEntry,
  ActionSummary,
  AlertRow,
  CompetitorDetailRow,
  CompetitorRow,
  EmergingBrandRow,
  EmergingStatus,
  KeywordSignalRow,
  KeywordSqpRow,
  SignalRow,
  SignalsPagination,
} from '../types/competitive'

export interface CompetitorsResponse {
  competitors: CompetitorRow[]
}

export interface SignalsResponse {
  signals: SignalRow[]
  signals_total?: number
  pagination: SignalsPagination
}

export interface EmergingBrandsResponse {
  brands: EmergingBrandRow[]
  total: number
}

export interface KeywordIntelResponse {
  sqp: KeywordSqpRow[]
  keywordSignals: KeywordSignalRow[]
}

export interface AlertsResponse {
  alerts: AlertRow[]
}

export interface CompetitorDetailResponse {
  competitor: CompetitorDetailRow | null
}

export interface DashboardKpiMetric {
  value: number
  delta: number | null
  trend: number[]
  total?: number
  previous?: number | null
  current?: number | null
}

export interface DashboardTopMover {
  competitor_id: number | null
  name: string | null
  score: number
  delta: number | null
  latest_bsr?: number | null
  previous_bsr?: number | null
  trend: number[]
}

export interface DashboardWeeklyTrend {
  activeThreats: number[]
  signals: number[]
}

export interface DashboardFreshness {
  threatsAt: string | null
  signalsAt: string | null
}

export interface DashboardTopThreatRow {
  competitor_id: number
  name: string
  alert_tier: string | null
  total_score: number
  score_delta_7d: number | null
  top_signal: string | null
  top_action: string | null
  latest_bsr: number | null
  score_history_7d: number[]
  actions: ActionSummary[]
}

export interface DashboardRecentSignalRow {
  id: number
  competitor_id: number | null
  competitor_name: string
  severity: string
  description: string | null
  detected_at: string
}

export interface DashboardRecentEmergingRow {
  id: number
  brand_name: string
  vitality_score: number
  keyword_overlap_terms: string[]
  created_at: string | null
}

export interface DashboardResponse {
  kpis: {
    activeThreats: DashboardKpiMetric
    topMover: DashboardTopMover | null
    buttahAmazonRank?: DashboardKpiMetric
    keywordDefense?: DashboardKpiMetric
    newSignalsToday: DashboardKpiMetric
    newEmergingBrands: DashboardKpiMetric
    weeklyTrend?: DashboardWeeklyTrend
  }
  freshness?: DashboardFreshness
  topThreats: DashboardTopThreatRow[]
  recentSignals: DashboardRecentSignalRow[]
  recentEmerging: DashboardRecentEmergingRow[]
}

export interface AIVisibilitySnapshot {
  id: number
  query_text: string
  platform: string
  brands_shown: string[]
  buttah_position: number | null
  competitor_ids: number[]
  snapshot_at: string
}

export interface AIVisibilityTopCompetitor {
  competitor_id: number
  competitor_name?: string
  mention_count: number
}

export interface AIVisibilitySummary {
  total_queries: number
  buttah_visible_count: number
  buttah_visibility_pct: number
  top_ai_competitors: AIVisibilityTopCompetitor[]
}

export interface AIVisibilityResponse {
  snapshots: AIVisibilitySnapshot[]
  summary: AIVisibilitySummary
  last_updated: string | null
}

export interface TrademarkFiling {
  id: number
  serial_number: string
  mark_text: string
  applicant_name: string
  filing_date: string
  status: string
  class_codes: string[]
  goods_desc: string
  relevance_score: number
  detected_at: string
}

export interface TrademarksResponse {
  filings: TrademarkFiling[]
  summary: {
    total_filings: number
    high_relevance: number
    new_this_week: number
  }
  last_updated: string | null
}

export interface WorkerHealthRow {
  worker_name: string
  last_run: string | null
  status: string
  rows_affected: number
  hours_since_run: number | null
  is_stale: boolean
}

export interface WorkerHealthResponse {
  workers: WorkerHealthRow[]
  overall_status: 'healthy' | 'degraded' | 'critical'
  stale_count: number
}

export interface BriefDataPoint {
  label: string
  value: string
  delta: string | null
  direction: 'up' | 'down' | 'flat'
}

export interface BriefTopThreat {
  competitor_id: number
  competitor_name: string
  threat_level: 'high' | 'medium' | 'low'
  headline: string
  detail: string
  data_points: BriefDataPoint[]
  recommended_actions: string[]
  top_action?: string | null
  urgency: 'act_now' | 'watch' | 'monitor'
}

export interface BriefKeywordAlert {
  keyword: string
  buttah_share: number
  competitor_share: number
  intruder_name: string
  intruder_asin: string | null
  recommended_action: string
}

export interface BriefEmergingBrandWatch {
  brand_name: string
  vitality_score: number
  why_it_matters: string
  recommended_action: string
}

export interface BriefWeekOverWeekSummary {
  total_signals: number
  critical_signals: number
  competitors_with_rising_scores: string[]
  competitors_with_falling_scores: string[]
  new_emerging_brands: number
}

export interface BriefData {
  brief_date: string
  generated_at: string
  executive_summary: string
  top_threats: BriefTopThreat[]
  keyword_alerts: BriefKeywordAlert[]
  emerging_brand_watch: BriefEmergingBrandWatch[]
  all_clear: string[]
  week_over_week_summary: BriefWeekOverWeekSummary
}

export interface BriefResponse {
  brief: BriefData | null
  generated_at: string | null
  generation_method: string | null
  brief_date: string | null
}

export interface BriefRegenerateResponse {
  queued: boolean
  requestedAt: string
}

export interface SignalClusterMetadata {
  signals?: Array<{
    id?: number
    description?: string
    severity?: string
    channel?: string
  }>
  channels?: string[]
  families?: Array<{
    keyword_family: string
    count: number
  }>
  [key: string]: unknown
}

export interface SignalCluster {
  id: number
  cluster_date: string
  competitor_id: number
  competitor_name: string
  cluster_type: string
  primary_channel: string
  signal_count: number
  max_severity: string
  narrative: string
  impact_score: number
  signal_ids: number[]
  metadata: SignalClusterMetadata
  created_at: string
}

export interface SignalClustersResponse {
  clusters: SignalCluster[]
  total_clusters: number
  total_signals: number
  worker: {
    has_run: boolean
    status: string | null
    last_run: string | null
  }
}

export type ActionItem = Action

export interface ActionsResponse {
  actions: Action[]
  counts: {
    pending: number
    in_progress: number
    done: number
  }
}

export interface ActionAuditLogResponse {
  action_id: number
  audit_log: ActionAuditEntry[]
}

export interface CompetitorKeywordOverlapRow {
  keyword: string
  buttah_share_pct: number
  competitor_share_pct: number
}

export interface CompetitorKeywordsResponse {
  keywords: CompetitorKeywordOverlapRow[]
}

export interface TikTokAdSignalRow {
  id: number
  competitor_id: number | null
  competitor_name: string
  video_id: string
  ad_type: string
  spend_tier: string | null
  has_tiktok_shop: boolean
  product_url: string | null
  caption_snippet: string | null
  view_count: number
  like_count: number
  detected_at: string
}

export interface TikTokHashtagBreakoutRow {
  id: number
  hashtag: string
  competitor_id: number | null
  competitor_name: string
  post_count_24h: number
  post_count_7d: number
  avg_views_per_post: number
  is_breakout: boolean
  snapshot_at: string
}

export interface TikTokPurchaseIntentVideoRow {
  id: number
  competitor_id: number | null
  competitor_name: string
  video_id: string | null
  content_url: string | null
  raw_text: string | null
  purchase_intent_score: number
  view_count: number
  like_count: number
  captured_at: string
}

export interface TikTokIntelResponse {
  adSignals: TikTokAdSignalRow[]
  hashtagBreakouts: TikTokHashtagBreakoutRow[]
  purchaseIntentVideos: TikTokPurchaseIntentVideoRow[]
}

export interface CreatorMentionRow {
  id: number
  competitor_id: number | null
  competitor_name: string
  video_id: string
  mention_type: string
  view_count: number
  like_count: number
  has_tiktok_shop: boolean
  caption_snippet: string | null
  mentioned_at: string
}

export interface CreatorRow {
  id: number
  handle: string
  platform: string
  follower_count: number
  follower_tier: string
  is_verified: boolean
  first_seen_at: string | null
  last_seen_at: string | null
  mention_count: number
  mention_count_7d: number
  recent_mentions: CreatorMentionRow[]
}

export interface CreatorConvergenceAlertRow {
  id: number
  competitor_id: number | null
  competitor_name: string
  severity: string
  description: string | null
  creator_count: number
  detected_at: string
}

export interface CreatorsResponse {
  creators: CreatorRow[]
  convergenceAlerts: CreatorConvergenceAlertRow[]
  topCreators: CreatorRow[]
}

export interface LivestreamEventRow {
  id: number
  competitor_id: number | null
  competitor_name: string
  platform: string
  creator_handle: string | null
  estimated_viewers: number
  has_tiktok_shop: boolean
  products_shown: string[]
  started_at: string
  duration_minutes: number | null
  detected_at: string
}

export interface LivestreamsResponse {
  livestreams: LivestreamEventRow[]
  shopLivestreams: LivestreamEventRow[]
  totalEvents: number
}

export interface ThreatScoreLayerBreakdown {
  anomaly_layer: number
  continuous_layer: number
  predictive_layer: number
}

export interface ScoreBreakdownRow {
  competitor_id: number
  competitor_name: string
  total_score: number
  score_date: string | null
  alert_tier: string | null
  layers: ThreatScoreLayerBreakdown
}

export interface ScoreBreakdownResponse {
  breakdown: ScoreBreakdownRow[]
}

export interface EventGraphNode {
  id: number
  node_type: 'anomaly_event' | 'viral_moment' | 'bsr_shift' | 'creator_surge' | string
  competitor_id: number | null
  competitor_name: string
  entity_label: string
  event_date: string
  severity: 'low' | 'medium' | 'high' | 'critical' | null | string
  source_table?: string
  source_id?: number
  metadata: Record<string, unknown> | null
}

export interface EventGraphEdge {
  id: number
  source_node_id: number
  target_node_id: number
  edge_type: 'same_competitor_burst' | 'competitor_response' | string
  weight: number
  metadata: Record<string, unknown> | null
}

export interface EventGraphSummary {
  total_nodes: number
  total_edges: number
  date_range: string
}

export interface EventGraphResponse {
  nodes: EventGraphNode[]
  edges: EventGraphEdge[]
  summary: EventGraphSummary
}

export interface CategoryIntelligenceRow {
  id: number
  metric_date: string
  total_tiktok_volume: number
  total_signal_count: number
  share_of_voice: Record<string, number> | null
  top_competitor_id: number | null
  top_competitor_name: string | null
  category_growth_rate: number | null
  category_health_score: number
  anomaly_density: number | null
  metadata: Record<string, unknown> | null
  created_at: string | null
}

export interface CategoryIntelResponse {
  latest: CategoryIntelligenceRow | null
  history: CategoryIntelligenceRow[]
  competitor_sov_summary: Record<string, number>
  sov_entries: Array<{
    brand: string
    share: number | null
    label: string
    is_buttah: boolean
    is_baseline: boolean
  }>
}

export interface EventGraphQueryParams {
  competitorId?: number | string
  nodeType?: string
  days?: number
}

export interface UpdateEmergingBrandStatusPayload {
  id: number
  status: EmergingStatus
}

export interface UpdateEmergingBrandDarkSkinRelevantPayload {
  id: number
  is_dark_skin_relevant: boolean
}

export interface UpdateEmergingBrandDarkSkinRelevantResponse {
  success: boolean
  brand: {
    id: number
    is_dark_skin_relevant: boolean
  }
}

export interface AddCompetitorPayload {
  brand_name: string
  amazon_brand_name: string
  tiktok_handle: string | null
  threat_tier: string
  notes: string
}

export interface SignalsQueryParams {
  page?: number
  limit?: number
  severity?: string
  channel?: string
  competitorId?: number | string
}

export interface SignalClustersQueryParams {
  date?: string
  severity?: string
  channel?: string
  competitorId?: number | string
}

async function fetchJson<T>(url: string): Promise<T> {
  // Strip leading /api prefix — apiClient.get expects the path after /api
  const endpoint = url.startsWith('/api') ? url.slice('/api'.length) : url
  return apiClient.get<T>(endpoint)
}

function buildSignalsQueryString(params: SignalsQueryParams = {}): string {
  const query = new URLSearchParams()

  query.set('page', String(params.page ?? 0))
  query.set('limit', String(params.limit ?? 25))

  if (params.severity) {
    query.set('severity', params.severity)
  }

  if (params.channel) {
    query.set('channel', params.channel)
  }

  if (params.competitorId !== undefined && params.competitorId !== null && String(params.competitorId).trim()) {
    query.set('competitor_id', String(params.competitorId))
  }

  return query.toString()
}

function buildSignalClustersQueryString(params: SignalClustersQueryParams = {}): string {
  const query = new URLSearchParams()

  if (params.date) {
    query.set('date', params.date)
  }

  if (params.severity) {
    query.set('severity', params.severity)
  }

  if (params.channel) {
    query.set('channel', params.channel)
  }

  if (params.competitorId !== undefined && params.competitorId !== null && String(params.competitorId).trim()) {
    query.set('competitor_id', String(params.competitorId))
  }

  return query.toString()
}

function buildEventGraphQueryString(params: EventGraphQueryParams = {}): string {
  const query = new URLSearchParams()

  query.set('days', String(params.days ?? 30))

  if (params.competitorId !== undefined && params.competitorId !== null && String(params.competitorId).trim()) {
    query.set('competitor_id', String(params.competitorId))
  }

  if (params.nodeType) {
    query.set('node_type', params.nodeType)
  }

  return query.toString()
}

export function useCompetitors() {
  return useQuery({
    queryKey: ['competitors'],
    queryFn: () => fetchJson<CompetitorsResponse>('/api/competitive/competitors'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useSignals(params: SignalsQueryParams = {}) {
  const queryParams = {
    page: params.page ?? 0,
    limit: params.limit ?? 25,
    severity: params.severity ?? '',
    channel: params.channel ?? '',
    competitorId: params.competitorId ?? '',
  }

  return useQuery({
    queryKey: ['signals', queryParams],
    queryFn: () => fetchJson<SignalsResponse>(`/api/competitive/signals?${buildSignalsQueryString(params)}`),
    staleTime: 2 * 60 * 1000,
  })
}

export function useSignalClusters(filters: SignalClustersQueryParams = {}) {
  const queryFilters = {
    date: filters.date ?? '',
    severity: filters.severity ?? '',
    channel: filters.channel ?? '',
    competitorId: filters.competitorId ?? '',
  }
  const queryString = buildSignalClustersQueryString(filters)
  const suffix = queryString ? `?${queryString}` : ''

  return useQuery({
    queryKey: ['competitive', 'signal-clusters', queryFilters],
    queryFn: () => fetchJson<SignalClustersResponse>(`/api/competitive/signal-clusters${suffix}`),
    staleTime: 5 * 60 * 1000,
  })
}

export function useActions(competitorFilter?: string | number, limit = 50, enabled = true) {
  const query = new URLSearchParams()
  query.set('limit', String(limit))

  if (typeof competitorFilter === 'number' && Number.isFinite(competitorFilter) && competitorFilter > 0) {
    query.set('competitor_id', String(competitorFilter))
  } else if (typeof competitorFilter === 'string' && competitorFilter.trim()) {
    query.set('competitor_name', competitorFilter.trim())
  }

  const queryString = query.toString()
  const suffix = queryString ? `?${queryString}` : ''

  return useQuery({
    queryKey: ['competitive', 'actions', competitorFilter ?? 'all', limit],
    queryFn: () => fetchJson<ActionsResponse>(`/api/competitive/actions${suffix}`),
    staleTime: 0,
    enabled,
  })
}

export function useActionAuditLog(actionId: number) {
  return useQuery({
    queryKey: ['competitive', 'actions', 'audit-log', actionId],
    queryFn: () => fetchJson<ActionAuditLogResponse>(`/api/competitive/actions/${actionId}/audit-log`),
    enabled: Number.isFinite(actionId) && actionId > 0,
    staleTime: 0,
  })
}

export function useCompetitorKeywords(competitorId: number) {
  return useQuery({
    queryKey: ['competitive', 'competitor-keywords', competitorId],
    queryFn: () =>
      fetchJson<CompetitorKeywordsResponse>(
        `/api/competitive/keyword-intel?competitor_id=${encodeURIComponent(String(competitorId))}`
      ),
    staleTime: 5 * 60 * 1000,
    enabled: Number.isFinite(competitorId) && competitorId > 0,
  })
}

export function useEmergingBrands() {
  return useQuery({
    queryKey: ['emerging-brands'],
    queryFn: () => fetchJson<EmergingBrandsResponse>('/api/competitive/emerging-brands'),
    staleTime: 10 * 60 * 1000,
  })
}

export function useKeywordIntel() {
  return useQuery({
    queryKey: ['keyword-intel'],
    queryFn: () => fetchJson<KeywordIntelResponse>('/api/competitive/keyword-intel'),
    staleTime: 10 * 60 * 1000,
  })
}

export function useAlerts(limit = 200) {
  return useQuery({
    queryKey: ['alerts', limit],
    queryFn: () => fetchJson<AlertsResponse>(`/api/competitive/alerts?limit=${limit}`),
    staleTime: 2 * 60 * 1000,
  })
}

export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: () => fetchJson<DashboardResponse>('/api/competitive/dashboard'),
    staleTime: 2 * 60 * 1000,
  })
}

export function useAIVisibility() {
  return useQuery({
    queryKey: ['ai-visibility'],
    queryFn: () => fetchJson<AIVisibilityResponse>('/api/competitive/ai-visibility'),
    staleTime: 60 * 60 * 1000,
  })
}

export function useTrademarks() {
  return useQuery({
    queryKey: ['trademarks'],
    queryFn: () => fetchJson<TrademarksResponse>('/api/competitive/trademarks'),
    staleTime: 60 * 60 * 1000,
  })
}

export function useWorkerHealth() {
  return useQuery({
    queryKey: ['competitive', 'worker-health'],
    queryFn: () => fetchJson<WorkerHealthResponse>('/api/competitive/health'),
    refetchInterval: 60_000,
  })
}

export function useTikTokIntel() {
  return useQuery({
    queryKey: ['competitive', 'tiktok-intel'],
    queryFn: () => apiClient.get<TikTokIntelResponse>('/competitive/tiktok-intel'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreators(competitorId?: number) {
  const params = competitorId ? `?competitor_id=${encodeURIComponent(String(competitorId))}` : ''

  return useQuery({
    queryKey: ['competitive', 'creators', competitorId ?? 'all'],
    queryFn: () => apiClient.get<CreatorsResponse>(`/competitive/creators${params}`),
    staleTime: 5 * 60 * 1000,
  })
}

export function useLivestreams(competitorId?: number) {
  const params = competitorId ? `?competitor_id=${encodeURIComponent(String(competitorId))}` : ''

  return useQuery({
    queryKey: ['competitive', 'livestreams', competitorId ?? 'all'],
    queryFn: () => apiClient.get<LivestreamsResponse>(`/competitive/livestreams${params}`),
    staleTime: 5 * 60 * 1000,
  })
}

export function useScoreBreakdown(competitorId?: number) {
  const params = competitorId ? `?competitor_id=${encodeURIComponent(String(competitorId))}` : ''

  return useQuery({
    queryKey: ['competitive', 'score-breakdown', competitorId ?? 'all'],
    queryFn: () => fetchJson<ScoreBreakdownResponse>(`/api/competitive/score-breakdown${params}`),
    staleTime: 5 * 60 * 1000,
  })
}

export function useEventGraph(params: EventGraphQueryParams = {}) {
  const queryParams = {
    competitorId: params.competitorId ?? '',
    nodeType: params.nodeType ?? '',
    days: params.days ?? 30,
  }
  const queryString = buildEventGraphQueryString(params)

  return useQuery({
    queryKey: ['competitive', 'event-graph', queryParams],
    queryFn: () => fetchJson<EventGraphResponse>(`/api/competitive/event-graph?${queryString}`),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCategoryIntel(days = 30) {
  const safeDays = Number.isFinite(days) && days > 0 ? Math.round(days) : 30

  return useQuery({
    queryKey: ['competitive', 'category-intel', safeDays],
    queryFn: () => fetchJson<CategoryIntelResponse>(`/api/competitive/category-intel?days=${safeDays}`),
    staleTime: 5 * 60 * 1000,
  })
}

export function useBrief(date?: string) {
  const params = date ? `?date=${encodeURIComponent(date)}` : ''

  return useQuery({
    queryKey: ['brief', date ?? 'latest'],
    queryFn: () => apiClient.get<BriefResponse>(`/competitive/briefs${params}`),
    staleTime: 5 * 60 * 1000,
  })
}

async function regenerateBrief() {
  return apiClient.post<BriefRegenerateResponse>('/competitive/briefs/regenerate', {})
}

export function useRegenerateBrief() {
  return useMutation({
    mutationFn: regenerateBrief,
  })
}

export function useCompetitorDetail(competitorId: number | null) {
  return useQuery({
    queryKey: ['competitor-detail', competitorId],
    queryFn: () => fetchJson<CompetitorDetailResponse>(`/api/competitive/competitors/${competitorId}`),
    staleTime: 5 * 60 * 1000,
    enabled: competitorId !== null,
  })
}

export interface Prediction {
  competitor_id: number
  brand_name: string
  probability_top5_90d: number
  model_version: string
  features_used: Record<string, number>
  computed_at: string
}

export function usePredictions() {
  const [predictions, setPredictions] = React.useState<Prediction[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    apiClient.get('/competitive/predictions')
      .then((data: unknown) => setPredictions(data as Prediction[]))
      .catch((err: unknown) => setError((err as Error).message))
      .finally(() => setLoading(false))
  }, [])

  return { predictions, loading, error }
}

async function patchEmergingBrandStatus(payload: UpdateEmergingBrandStatusPayload) {
  await apiClient.patch(`/competitive/emerging-brands/${payload.id}`, { status: payload.status })
}

async function patchEmergingBrandDarkSkinRelevant(payload: UpdateEmergingBrandDarkSkinRelevantPayload) {
  return apiClient.patch<UpdateEmergingBrandDarkSkinRelevantResponse>(
    `/competitive/emerging-brands/${payload.id}`,
    { is_dark_skin_relevant: payload.is_dark_skin_relevant }
  )
}

async function addCompetitor(payload: AddCompetitorPayload) {
  await apiClient.post('/competitive/competitors', payload)
}

async function retryAlert(alertId: number) {
  await apiClient.post('/competitive/alerts', { id: alertId })
}

export function useUpdateEmergingBrandStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: patchEmergingBrandStatus,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['emerging-brands'] })
    },
  })
}

export function useUpdateEmergingBrandDarkSkinRelevant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: patchEmergingBrandDarkSkinRelevant,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['emerging-brands'] })
    },
  })
}

export function useAddCompetitor() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: addCompetitor,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['competitors'] })
    },
  })
}

export function useRetryAlert() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: retryAlert,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })
}
